import { createContext, useContext, useState, useEffect, useCallback } from "react";

const ThemeContext = createContext();

export const THEMES = {
  dark: {
    name: "Dark",
    background: "#19191D",
    backgroundSecondary: "var(--app-input-bg)",
    backgroundTertiary: "#27272A",
    foreground: "#FAFAFA",
    foregroundSecondary: "#A1A1AA",
    foregroundMuted: "var(--app-text-muted)",
    border: "#27272A",
    borderHover: "#3F3F46",
    primary: "#0A84FF",
    primaryHover: "#0070E0",
    card: "var(--app-input-bg)",
    cardHover: "#1a1a1e",
    sidebar: "#0f0f11",
    sidebarBorder: "#27272A",
    input: "var(--app-input-bg)",
    inputBorder: "#27272A",
    success: "var(--app-green)",
    warning: "var(--app-amber)",
    error: "var(--app-red)",
    // CSS variables
    cssVars: {
      "--background": "240 5% 11%",
      "--foreground": "0 0% 98%",
      "--card": "240 4% 10%",
      "--card-foreground": "0 0% 98%",
      "--popover": "240 4% 10%",
      "--popover-foreground": "0 0% 98%",
      "--primary": "211 100% 50%",
      "--primary-foreground": "0 0% 100%",
      "--secondary": "240 4% 16%",
      "--secondary-foreground": "0 0% 90%",
      "--muted": "240 4% 16%",
      "--muted-foreground": "240 5% 46%",
      "--accent": "240 4% 16%",
      "--accent-foreground": "0 0% 98%",
      "--destructive": "0 85% 60%",
      "--destructive-foreground": "0 0% 100%",
      "--border": "240 4% 16%",
      "--input": "240 4% 16%",
      "--ring": "211 100% 50%",
    }
  },
  light: {
    name: "Light",
    background: "#F8F9FB",
    backgroundSecondary: "#F1F5F9",
    backgroundTertiary: "#E2E8F0",
    foreground: "#0F172A",
    foregroundSecondary: "#475569",
    foregroundMuted: "#94A3B8",
    border: "#E2E8F0",
    borderHover: "#CBD5E1",
    primary: "#0066FF",
    primaryHover: "#0052CC",
    card: "#FFFFFF",
    cardHover: "#F8FAFC",
    sidebar: "#FFFFFF",
    sidebarBorder: "#E2E8F0",
    input: "#FFFFFF",
    inputBorder: "#CBD5E1",
    success: "#059669",
    warning: "#D97706",
    error: "#DC2626",
    cssVars: {
      "--background": "216 20% 98%",
      "--foreground": "222 47% 11%",
      "--card": "0 0% 100%",
      "--card-foreground": "222 47% 11%",
      "--popover": "0 0% 100%",
      "--popover-foreground": "222 47% 11%",
      "--primary": "220 100% 50%",
      "--primary-foreground": "0 0% 100%",
      "--secondary": "210 40% 96%",
      "--secondary-foreground": "222 47% 11%",
      "--muted": "210 40% 96%",
      "--muted-foreground": "215 16% 47%",
      "--accent": "210 40% 96%",
      "--accent-foreground": "222 47% 11%",
      "--destructive": "0 85% 60%",
      "--destructive-foreground": "0 0% 100%",
      "--border": "214 32% 91%",
      "--input": "214 32% 91%",
      "--ring": "220 100% 50%",
    }
  }
};

export const SIDEBAR_THEMES = {
  default: { name: "Default", desc: "Follows app theme" },
  dark: { name: "Dark", desc: "Always dark sidebar" },
  light: { name: "Light", desc: "Always light sidebar" },
};

const SIDEBAR_DARK = {
  bg: "#0f0f11", text: "#FAFAFA", textSecondary: "#C4C4CC", textMuted: "#A1A1AA",
  border: "#27272A", surface: "#1a1a1e", accent: "#0A84FF", groupLabel: "#A1A1AA",
  linkActive: "#0A84FF", linkActiveBg: "rgba(10,132,255,0.1)",
};
const SIDEBAR_LIGHT = {
  bg: "#FFFFFF", text: "#0F172A", textSecondary: "#334155", textMuted: "#475569",
  border: "#E2E8F0", surface: "#F1F5F9", accent: "#0066FF", groupLabel: "#64748B",
  linkActive: "#0066FF", linkActiveBg: "rgba(0,102,255,0.08)",
};

export function ThemeProvider({ children }) {
  const [themeName, setThemeName] = useState(() => {
    const saved = localStorage.getItem("cineflow-theme");
    return saved || "light";
  });
  const [sidebarThemeName, setSidebarThemeName] = useState(() => {
    const saved = localStorage.getItem("cineflow-sidebar-theme");
    return saved || "dark";
  });

  const theme = THEMES[themeName] || THEMES.dark;

  const sidebarColors = sidebarThemeName === "light" ? SIDEBAR_LIGHT
    : sidebarThemeName === "dark" ? SIDEBAR_DARK
    : (themeName === "light" ? SIDEBAR_LIGHT : SIDEBAR_DARK);

  useEffect(() => {
    localStorage.setItem("cineflow-theme", themeName);
    
    // Apply CSS variables to root
    const root = document.documentElement;
    Object.entries(theme.cssVars).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });
    
    // Apply body styles
    document.body.style.background = theme.background;
    document.body.style.color = theme.foreground;
    
    // Add theme class to body
    document.body.classList.remove("theme-dark", "theme-light");
    document.body.classList.add(`theme-${themeName}`);
  }, [themeName, theme]);

  useEffect(() => {
    localStorage.setItem("cineflow-sidebar-theme", sidebarThemeName);
  }, [sidebarThemeName]);

  const toggleTheme = useCallback(() => {
    setThemeName(prev => {
      const next = prev === "dark" ? "light" : "dark";
      // Persist to server
      try { 
        const token = document.cookie.split(';').find(c => c.trim().startsWith('session_token='));
        if (token) {
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/user-preference`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ key: 'theme', value: next })
          }).catch(() => {});
        }
      } catch {}
      return next;
    });
  }, []);

  const setTheme = (name) => {
    if (THEMES[name]) {
      setThemeName(name);
    }
  };

  const setSidebarTheme = (name) => {
    if (SIDEBAR_THEMES[name]) {
      setSidebarThemeName(name);
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, themeName, setTheme, toggleTheme, sidebarThemeName, setSidebarTheme, sidebarColors }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

export default ThemeContext;
